// =============================================================================
// ui/tabs/Backtests.tsx — the Backtesting tab. "Edge Development".
// -----------------------------------------------------------------------------
// A manual back-testing tracker: record how a PATTERN played out on your own
// charts — points and R only, no dollars, no discipline fields — and put those
// hand-recorded samples beside the SAME pattern's real trades. Every number here
// is computed in `selectors/backtests` (→ `domain/backtests`); this file only
// renders and edits.
//
// ── WHAT IS PORTED ──
//   the groups + the Backtest-vs-Live stats table   (the verification gate)
//   the inline sample editor, save-on-blur           (all fields + R:R toggle)
//   add a sample · start testing a pattern · delete · Promote a candidate
//   confluences (pick existing) · screenshots (reused grid)
//
// ── WHAT IS DEFERRED, AND SAID ON SCREEN (a blank reads as broken; a labelled
//    gap reads as unfinished) ──
//   Import from trades / Manual Import / multi-select Bulk edit — each its own
//   surface — and the ENTRY-CRITERIA checklist, for the reason the trade form
//   defers it too: criteria resolve against pattern rule VERSIONS this client
//   does not load, and ticking one against the wrong version misattributes it.
//   Stored criteria marks are preserved — the form never sends `criteriaMet`.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import { useAccounts } from '../state/accounts';
import { useBacktests } from '../state/backtests';
import { useContracts } from '../state/contracts';
import { useExitModels } from '../state/exitModels';
import { useFilters } from '../state/filters';
import { usePatterns } from '../state/patterns';
import { usePreferences } from '../state/preferences';
import { useSupabase } from '../state/supabase';
import { useTradeRows } from '../state/useTradeRows';
import {
  backtestDerived, backtestLiveUniverse, buildBacktestGroups,
  fmtPct, fmtPts, fmtR, fmtRatio, newBacktestRecord, sampleBadge,
  untestedPatterns, versionTestedFor,
  type BacktestGroup, type BacktestRecord, type BacktestStats,
} from '../../selectors/backtests';
import {
  activeConfluenceIds, confluenceRegistry, confluencesToStore, type ConfluenceNode,
} from '../../selectors/confluences';
import { REGIMES, SESSIONS, autoSessionLabel } from '../../selectors/tradeForm';
import { patternLifecycle } from '../../io/sync/patterns';
import { savePatternLifecycle } from '../../io/sync/savePattern';
import { ScreenshotGrid } from '../media/ScreenshotGrid';

const UNBOUNDED = { startDate: null, endDate: null };

type RrUnit = 'r' | 'points';

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function BacktestsTab() {
  const { sb, userId } = useSupabase();
  const bt = useBacktests();
  const { patterns, reload: reloadPatterns } = usePatterns();
  const { contracts } = useContracts();
  const { exitModels } = useExitModels();
  const { prefs } = usePreferences();
  const { accounts } = useAccounts();
  const { accounts: selection } = useFilters();
  const mode = prefs?.dayBoundaryMode ?? null;
  const { rows } = useTradeRows(UNBOUNDED, mode);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [pick, setPick] = useState('');
  // ── the R:R unit ───────────────────────────────────────────────────────────
  // ⚠ SEEDED FROM `appFlags.plannedTargetUnit` AND DELIBERATELY NOT WRITTEN
  // BACK, so flipping it does not survive leaving the tab. That is a real
  // difference from beta, which shares one saved preference between here and the
  // trade form — and it is the same call `TradeDetail.tsx` makes, in its own
  // words: "persisting it means a preferences write from inside a trade save,
  // and a failed one would be reported as a failed trade save."
  //
  // 🔴 TCR-12 ASKED FOR THIS TO PERSIST AND IT WAS NOT DONE. Making this tab
  // write back would leave the two R:R toggles in the client behaving
  // differently from each other, which is worse than both differing from beta
  // the same way. If it should change, it changes in BOTH places and gets a
  // decision entry — it is not a Backtesting fix.
  const [unit, setUnit] = useState<RrUnit>(
    () => (prefs?.appFlags?.['plannedTargetUnit'] === 'points' ? 'points' : 'r'),
  );

  const registry = useMemo(() => confluenceRegistry(prefs?.confluences), [prefs]);
  const live = useMemo(
    () => (rows ? backtestLiveUniverse(rows, selection, accounts ?? []) : []),
    [rows, selection, accounts],
  );
  const records = bt.records ?? [];
  const groups = useMemo(
    () => buildBacktestGroups(records, patterns, live),
    [records, patterns, live],
  );
  const untested = useMemo(() => untestedPatterns(records, patterns), [records, patterns]);

  const makeRecord = useCallback((patternId: string): BacktestRecord => newBacktestRecord(patternId, {
    id: crypto.randomUUID(),
    nowIso: new Date().toISOString(),
    today: todayIso(),
    existing: records,
    contracts: contracts ?? [],
    defaultRisk: prefs?.defaultRiskByInstrument ?? null,
    versionTested: versionTestedFor(patterns, patternId),
  }), [records, contracts, prefs, patterns]);

  const addSample = useCallback((patternId: string) => {
    const rec = makeRecord(patternId);
    setCollapsed((c) => ({ ...c, [patternId]: false }));
    setExpandedId(rec.id);
    void bt.add(rec);
  }, [makeRecord, bt]);

  const startTesting = useCallback(() => {
    if (!pick) return;
    addSample(pick);
    setPick('');
  }, [pick, addSample]);

  const del = useCallback((id: string) => {
    if (!window.confirm('Delete this backtest sample? This can’t be undone.')) return;
    if (expandedId === id) setExpandedId(null);
    void bt.remove(id);
  }, [bt, expandedId]);

  const commit = useCallback((rec: BacktestRecord) => { void bt.update(rec); }, [bt]);

  const promote = useCallback(async (patternId: string) => {
    const p = (patterns ?? []).find((x) => x.id === patternId);
    if (!p) return;
    if (!window.confirm(
      `Promote "${p.name}" to production? It becomes taggable on live trades and shows in your live analytics.`,
    )) return;
    // beta's `promotePattern`: flip status to production, versions untouched.
    const lc = patternLifecycle(p);
    const res = await savePatternLifecycle(
      sb, p.id, p,
      {
        status: 'production', versions: lc.versions,
        liveVersion: lc.liveVersion, candidateVersion: lc.candidateVersion,
        ...(lc.createdAt !== undefined ? { createdAt: lc.createdAt } : {}),
      },
      userId, null,
    );
    if (res.error) { window.alert(`Couldn’t promote: ${res.error}`); return; }
    reloadPatterns();
  }, [patterns, sb, userId, reloadPatterns]);

  if (bt.phase === 'loading') {
    return <div className="backtests-tab"><p className="muted" role="status">Loading your backtests…</p></div>;
  }
  if (bt.phase === 'failed') {
    return (
      <div className="backtests-tab">
        <div role="alert" className="import-alert">
          <b>Couldn’t load your backtests.</b> {bt.error}
        </div>
      </div>
    );
  }

  return (
    <div className="backtests-tab">
      <div className="bt-header">
        <h3>Backtesting</h3>
        <div className="bt-picker">
          <button type="button" className="btn" disabled title="Copying live trades in as samples isn’t ported yet.">↧ Import from trades</button>
          <button type="button" className="btn" disabled title="Spreadsheet / CSV import isn’t ported yet.">⤓ Manual Import</button>
          <button type="button" className="btn" disabled title="Multi-select bulk edit isn’t ported yet.">☑ Select</button>
          <select value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Pick a pattern to test">
            <option value="">Pick a pattern to test…</option>
            {untested.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button type="button" className="btn primary" onClick={startTesting} disabled={!pick}>Start testing</button>
          <button type="button" className="btn" disabled title="Creating a candidate pattern here isn’t ported yet — add it on the Patterns tab.">+ New pattern</button>
        </div>
      </div>

      <p className="bt-blurb muted">
        Find the setup on your own charts, record what happened, prove the edge before you risk it.
        Points + R only — no dollars, no discipline fields. Stats use the same definitions as the live analytics.
      </p>

      {groups.length === 0 ? (
        <div className="card bt-empty">
          <div className="bt-empty-t">No patterns under test yet.</div>
          <div className="muted">
            {(patterns ?? []).length
              ? 'Pick a pattern above and record your first historical sample.'
              : 'Create a pattern on the Patterns tab first — backtests attach to a pattern.'}
          </div>
        </div>
      ) : null}

      {groups.map((g) => (
        <GroupCard
          key={g.patternId || '(none)'}
          group={g}
          collapsed={!!collapsed[g.patternId]}
          expandedId={expandedId}
          unit={unit}
          registry={registry}
          contracts={contracts ?? []}
          exitModels={exitModels ?? []}
          onToggleCollapse={() => setCollapsed((c) => ({ ...c, [g.patternId]: !c[g.patternId] }))}
          onAddSample={() => addSample(g.patternId)}
          onToggleExpand={(id) => setExpandedId((cur) => (cur === id ? null : id))}
          onDelete={del}
          onCommit={commit}
          onFlipUnit={setUnit}
          onPromote={() => void promote(g.patternId)}
        />
      ))}
    </div>
  );
}

// ── one pattern group ────────────────────────────────────────────────────────

function GroupCard(props: {
  group: BacktestGroup;
  collapsed: boolean;
  expandedId: string | null;
  unit: RrUnit;
  registry: readonly ConfluenceNode[];
  contracts: readonly { symbol: string; name: string }[];
  exitModels: readonly { id: string; name: string }[];
  onToggleCollapse: () => void;
  onAddSample: () => void;
  onToggleExpand: (id: string) => void;
  onDelete: (id: string) => void;
  onCommit: (rec: BacktestRecord) => void;
  onFlipUnit: (u: RrUnit) => void;
  onPromote: () => void;
}) {
  const { group: g, collapsed } = props;
  const badge = sampleBadge(g.records.length);

  return (
    <div className="card bt-group">
      <div className="bt-group-head" onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        props.onToggleCollapse();
      }}>
        <span className="muted bt-caret">{collapsed ? '▸' : '▾'}</span>
        <h3>{g.name}</h3>
        {g.isCandidate ? <span className="bt-cand" title="Not taggable on live trades until you promote it">🧪 candidate</span> : null}
        <span className={`bt-n ${badge.cls}`} title={badge.hint}>n={g.records.length}</span>
        <span className="grow" />
        {g.isCandidate ? (
          <button type="button" className="btn primary" onClick={props.onPromote}
            title="Promote to production — makes it taggable on live trades and shows in live analytics">▲ Promote</button>
        ) : null}
        <button type="button" className="btn" onClick={props.onAddSample}>+ Add sample</button>
      </div>

      {collapsed ? null : (
        <>
          <StatsTable stats={g.stats} live={g.live} />
          {g.records.map((rec) => (
            <RecordRow
              key={rec.id}
              record={rec}
              expanded={props.expandedId === rec.id}
              unit={props.unit}
              registry={props.registry}
              contracts={props.contracts}
              exitModels={props.exitModels}
              onToggle={() => props.onToggleExpand(rec.id)}
              onDelete={() => props.onDelete(rec.id)}
              onCommit={props.onCommit}
              onFlipUnit={props.onFlipUnit}
            />
          ))}
        </>
      )}
    </div>
  );
}

// ── the Backtest-vs-Live comparison ──────────────────────────────────────────

function StatsTable({ stats, live }: { stats: BacktestStats; live: BacktestStats }) {
  const cells = (s: BacktestStats) => (
    <>
      <td>{s.n}</td>
      <td>{fmtPct(s.winRate)}</td>
      <td title={`mean R over ${s.nR} record${s.nR === 1 ? '' : 's'} with a stop`}>{fmtR(s.avgR)}</td>
      <td title={`avg MFE ${fmtPts(s.avgMfe)} / avg MAE ${fmtPts(s.avgMae)} pts`}>{fmtRatio(s.eRatio)}</td>
      <td>{fmtPct(s.medCapture)}</td>
    </>
  );
  return (
    <table className="bt-stats">
      <thead>
        <tr><th /><th>SAMPLES</th><th>WIN RATE</th><th>AVG R</th><th>E-RATIO</th><th>MFE CAP</th></tr>
      </thead>
      <tbody>
        <tr><td>Backtest</td>{cells(stats)}</tr>
        {live.n > 0 ? <tr className="live"><td>Live</td>{cells(live)}</tr> : null}
      </tbody>
    </table>
  );
}

// ── one sample: summary row + expandable editor ──────────────────────────────

function RecordRow(props: {
  record: BacktestRecord;
  expanded: boolean;
  unit: RrUnit;
  registry: readonly ConfluenceNode[];
  contracts: readonly { symbol: string; name: string }[];
  exitModels: readonly { id: string; name: string }[];
  onToggle: () => void;
  onDelete: () => void;
  onCommit: (rec: BacktestRecord) => void;
  onFlipUnit: (u: RrUnit) => void;
}) {
  const { record: b, expanded } = props;
  const d = backtestDerived(b);
  const em = props.exitModels.find((m) => m.id === b.exitModelId);
  const shots = b.screenshots.length;
  const side = b.side === 'long' || b.side === 'short' ? b.side : '';
  const rCls = d.r == null ? 'none' : (d.r >= 0 ? 'pos' : 'neg');

  return (
    <div className="bt-rec">
      <div className="bt-rec-sum" onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        props.onToggle();
      }}>
        <span className={`bt-out ${d.outcome ?? 'open'}`} title={d.outcome ?? 'incomplete — needs side + entry + exit'} />
        <span className="muted">{b.date || '—'}</span>
        {side
          ? <span className={`bt-side ${side}`}>{side.toUpperCase()}</span>
          : <span className="muted">side?</span>}
        <span className="bt-px">{b.contract} {b.entry ?? '—'} → {b.exit ?? '—'}</span>
        <span className="muted">stop {b.riskPoints != null ? `${b.riskPoints}pt` : '—'}</span>
        <span className={`bt-r ${rCls}`}>{fmtR(d.r)}</span>
        <span className="muted">{d.points == null ? '' : `${fmtPts(d.points)}pts`}</span>
        {b.versionTested != null
          ? <span className="bt-ver" title={`Backtest of rule-version ${b.versionTested}`}>v{b.versionTested}</span>
          : null}
        {em ? <span className="muted">{em.name}</span> : null}
        {shots ? <span className="muted">📷{shots}</span> : null}
        <span className="grow" />
        <button type="button" className="btn danger bt-del" title="Delete record" onClick={props.onDelete}>×</button>
        <span className="muted">{expanded ? '▾' : '▸'}</span>
      </div>
      {expanded ? (
        <div className="bt-rec-body">
          <RecordBody
            key={b.id}
            record={b}
            unit={props.unit}
            registry={props.registry}
            contracts={props.contracts}
            exitModels={props.exitModels}
            onCommit={props.onCommit}
            onFlipUnit={props.onFlipUnit}
          />
        </div>
      ) : null}
    </div>
  );
}

const NUM_FIELDS = ['entry', 'exit', 'riskPoints', 'rrPlanned', 'maePoints', 'mfePoints'] as const;
type NumField = (typeof NUM_FIELDS)[number];

function RecordBody(props: {
  record: BacktestRecord;
  unit: RrUnit;
  registry: readonly ConfluenceNode[];
  contracts: readonly { symbol: string; name: string }[];
  exitModels: readonly { id: string; name: string }[];
  onCommit: (rec: BacktestRecord) => void;
  onFlipUnit: (u: RrUnit) => void;
}) {
  const { unit } = props;
  const [draft, setDraft] = useState<BacktestRecord>(props.record);
  const d = backtestDerived(draft);

  // ⚠ Read here rather than drilled down from the tab. The session a time falls
  // in depends on the trader's session timezone, and threading one string
  // through `Group` → `RecordRow` → here would add a prop to two components
  // that have no other use for it.
  const { prefs } = usePreferences();
  const autoSession = autoSessionLabel(
    draft.timeIn, draft.date, prefs?.sessionTimezone ?? undefined,
  );

  const patch = (p: Partial<BacktestRecord>) => setDraft((cur) => ({ ...cur, ...p }));
  const commit = (next: BacktestRecord) => props.onCommit(next);
  // ⚠ Text/number persist on BLUR (matching beta's change-fires-on-blur); selects,
  // toggles and screenshots persist immediately.
  const patchAndCommit = (p: Partial<BacktestRecord>) => {
    const next = { ...draft, ...p };
    setDraft(next);
    commit(next);
  };

  // Number edits, with beta's R:R-in-points interplay ported verbatim.
  const editNum = (field: NumField, raw: string) => {
    const numVal = (raw === '' || !Number.isFinite(Number(raw))) ? null : Number(raw);
    if (unit === 'points' && field === 'rrPlanned') {
      const rp = Number(draft.riskPoints) || 0;
      patch({ rrPlanned: (numVal && rp) ? Number((numVal / rp).toFixed(4)) : (numVal == null ? null : 0) });
    } else if (unit === 'points' && field === 'riskPoints') {
      const reward = (Number(draft.rrPlanned) || 0) * (Number(draft.riskPoints) || 0);
      patch({ riskPoints: numVal, rrPlanned: numVal ? Number((reward / numVal).toFixed(4)) : (draft.rrPlanned == null ? null : 0) });
    } else {
      patch({ [field]: numVal } as Partial<BacktestRecord>);
    }
  };

  const rrpDisplay = unit === 'points'
    ? (draft.rrPlanned != null && draft.riskPoints ? Number((draft.rrPlanned * draft.riskPoints).toFixed(2)) : '')
    : (draft.rrPlanned == null ? '' : draft.rrPlanned);

  const active = activeConfluenceIds(draft.confluences);
  const setConfluences = (next: Set<string>) =>
    patchAndCommit({ confluences: confluencesToStore(props.registry, next, draft.confluences) });

  return (
    <>
      <div className="bt-fgrid">
        <label>DATE
          <input type="date" value={draft.date} onChange={(e) => patch({ date: e.target.value })} onBlur={() => commit(draft)} />
        </label>
        <label>TIME IN
          <input type="time" step={1} value={draft.timeIn} onChange={(e) => patch({ timeIn: e.target.value })} onBlur={() => commit(draft)} />
        </label>
        <label>TIME OUT
          <input type="time" step={1} value={draft.timeOut} onChange={(e) => patch({ timeOut: e.target.value })} onBlur={() => commit(draft)} />
        </label>
        <label>CONTRACT
          <select value={draft.contract} onChange={(e) => patchAndCommit({ contract: e.target.value })}>
            {draft.contract && !props.contracts.some((c) => c.symbol === draft.contract)
              ? <option value={draft.contract}>{draft.contract}</option> : null}
            {props.contracts.map((c) => <option key={c.symbol} value={c.symbol}>{c.symbol}</option>)}
          </select>
        </label>
        <label>SIDE
          <select value={draft.side} onChange={(e) => patchAndCommit({ side: e.target.value })}>
            <option value="">—</option>
            <option value="long">Long</option>
            <option value="short">Short</option>
          </select>
        </label>
        <NumInput label="ENTRY" value={draft.entry} step={0.25} onChange={(v) => editNum('entry', v)} onCommit={() => commit(draft)} />
        <NumInput label="EXIT" value={draft.exit} step={0.25} onChange={(v) => editNum('exit', v)} onCommit={() => commit(draft)} />
        <NumInput label="STOP (PTS)" value={draft.riskPoints} step={0.25} onChange={(v) => editNum('riskPoints', v)} onCommit={() => commit(draft)} />
        <label>{unit === 'points' ? 'REWARD (PTS)' : 'R:R PLANNED'}
          <span className="rrp-unit-toggle" title="Type your target as an R:R ratio or as a points distance">
            <button type="button" className={unit === 'r' ? 'on' : ''} onClick={() => props.onFlipUnit('r')}>R</button>
            <button type="button" className={unit === 'points' ? 'on' : ''} onClick={() => props.onFlipUnit('points')}>pts</button>
          </span>
          <input
            type="number" step={unit === 'points' ? 0.25 : 0.1} value={rrpDisplay}
            onChange={(e) => editNum('rrPlanned', e.target.value)} onBlur={() => commit(draft)}
          />
        </label>
        <NumInput label="MAE (PTS)" value={draft.maePoints} step={0.25} onChange={(v) => editNum('maePoints', v)} onCommit={() => commit(draft)} />
        <NumInput label="MFE (PTS)" value={draft.mfePoints} step={0.25} onChange={(v) => editNum('mfePoints', v)} onCommit={() => commit(draft)} />
        <label>EXIT MODEL
          <select value={draft.exitModelId} onChange={(e) => patchAndCommit({ exitModelId: e.target.value })}>
            <option value="">—</option>
            {props.exitModels.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            {draft.exitModelId && !props.exitModels.some((m) => m.id === draft.exitModelId)
              ? <option value={draft.exitModelId}>{`(exit model ${draft.exitModelId})`}</option> : null}
          </select>
        </label>
        <label>SESSION
          <select value={draft.session} onChange={(e) => patchAndCommit({ session: e.target.value })}>
            {/* 🎯 "Auto (NY-AM)" — WHICH session it will pick, not merely that
                it will pick one. Beta shows the resolved label here and
                refreshes it as the entry time is edited
                (app/index.html:31622). A bare "Auto" leaves the reader to work
                out the rule themselves, on the one control whose whole job is
                to say what it decided.

                ⚠ Falls back to a plain "Auto" when the time is blank or
                unparseable — `sessionFromTime` returns '' there, and inventing
                a session for a time nobody entered would be worse than saying
                nothing. */}
            <option value="">{autoSession}</option>
            {SESSIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>REGIME
          <select value={draft.regime} onChange={(e) => patchAndCommit({ regime: e.target.value })}>
            <option value="">—</option>
            {REGIMES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label>NEWS EVENT
          <input type="text" value={draft.newsEvent} placeholder="CPI, FOMC…" onChange={(e) => patch({ newsEvent: e.target.value })} onBlur={() => commit(draft)} />
        </label>
      </div>

      <div className="bt-derived">
        <span>Points: <b>{d.points == null ? '—' : fmtPts(d.points)}</b></span>
        <span>R: <b>{fmtR(d.r)}</b></span>
        <span>MFE captured: <b>{fmtPct(d.mfeCapture)}</b></span>
        <span>Outcome: <b>{d.outcome ?? 'incomplete'}</b></span>
      </div>

      <div className="bt-body-cols">
        <div>
          <div className="bt-sec-label">ENTRY CRITERIA</div>
          <p className="muted crit-empty">
            The entry-criteria checklist isn’t ported yet — it resolves against pattern rule versions this
            client doesn’t load. Any criteria already recorded on this sample are preserved.
          </p>
          <div className="bt-conf-block">
            <div className="bt-sec-label">CONFLUENCES</div>
            <p className="muted field-hint">
              Check the tools that confirmed the setup. Tools with sub-confluences (▸) expand on click —
              pick the specifics and the parent tags itself.
            </p>
            <ConfluencePicker registry={props.registry} active={active} onChange={setConfluences} />
          </div>
        </div>
        <div>
          <div className="bt-sec-label">NOTES</div>
          <textarea
            className="bt-notes" rows={3} value={draft.notes}
            placeholder="What did the setup look like? What would you refine?"
            onChange={(e) => patch({ notes: e.target.value })} onBlur={() => commit(draft)}
          />
          <div className="bt-sec-label">SCREENSHOT</div>
          <ScreenshotGrid
            files={draft.screenshots}
            prefix={`bt-${draft.date || todayIso()}-${draft.id}`}
            onChange={(next) => patchAndCommit({ screenshots: next })}
          />
        </div>
      </div>
    </>
  );
}

function NumInput(props: {
  label: string; value: number | null; step: number;
  onChange: (raw: string) => void; onCommit: () => void;
}) {
  return (
    <label>{props.label}
      <input
        type="number" step={props.step} value={props.value ?? ''}
        onChange={(e) => props.onChange(e.target.value)} onBlur={props.onCommit}
      />
    </label>
  );
}

// ── confluences: pick existing (adding new lives in Settings, not ported) ─────

function ConfluencePicker(
  { registry, active, onChange }:
  { registry: readonly ConfluenceNode[]; active: ReadonlySet<string>; onChange: (next: Set<string>) => void },
) {
  const [override, setOverride] = useState<ReadonlyMap<string, boolean>>(new Map());
  const autoOpen = (c: ConfluenceNode) =>
    active.has(c.id) || c.children.some((ch) => active.has(ch.id));
  const toggle = (id: string) => {
    const next = new Set(active);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  };

  return (
    <div className="confluence-grid">
      {registry.map((c) => {
        const kidsOn = c.children.filter((ch) => active.has(ch.id)).length;
        const on = active.has(c.id) || kidsOn > 0;
        const expanded = override.get(c.id) ?? autoOpen(c);
        const flip = () => setOverride((m) => new Map(m).set(c.id, !expanded));
        return (
          <div key={c.id}>
            <div
              className={`confluence-row${on ? ' checked' : ''}${c.children.length ? ' conf-parent' : ''}`}
              onClick={c.children.length ? flip : undefined}
            >
              {c.children.length ? (
                <>
                  <input type="checkbox" checked={on} readOnly tabIndex={-1} aria-label={c.label} className="conf-derived" />
                  <span className="conf-name" title={c.hint ?? ''}>{c.label}</span>
                  <span className="grow" />
                </>
              ) : (
                <label>
                  <input type="checkbox" checked={on} onChange={() => toggle(c.id)} />
                  <span className="conf-name" title={c.hint ?? ''}>{c.label}</span>
                </label>
              )}
              {c.children.length ? (
                <button
                  type="button" className="conf-chevron" aria-expanded={expanded}
                  aria-label={`${expanded ? 'Collapse' : 'Expand'} ${c.label}`}
                  onClick={(e) => { e.stopPropagation(); flip(); }}
                >{expanded ? '▾' : '▸'}</button>
              ) : null}
            </div>
            {c.children.length && expanded ? (
              <div className="conf-children">
                {c.children.map((ch) => (
                  <label key={ch.id} className="conf-child">
                    <input type="checkbox" checked={active.has(ch.id)} onChange={() => toggle(ch.id)} />
                    <span>{ch.label}</span>
                  </label>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
